import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { FaCheck, FaEdit, FaFilter, FaSearch, FaTrash } from 'react-icons/fa';
import { apiClient } from '@/services/apiClient';

interface ResourceComplianceQuestion {
  questionId: number;
  source: 'faq' | 'community';
  questionText: string;
  answerText?: string | null;
  askedByUser?: string | null;
  askedByDisplayName?: string | null;
  askedByEmail?: string | null;
  displayAsAnonymous: boolean;
  isPublic: boolean;
  isAnswered: boolean;
  category?: string | null;
  helpfulCount: number;
  sortOrder?: number | null;
  answeredByUser?: string | null;
  answeredDt?: string | null;
  createDt: string;
  lastUpdateDt: string;
}

interface EditState {
  questionText: string;
  answerText: string;
  askedByUser: string;
  askedByDisplayName: string;
  askedByEmail: string;
  displayAsAnonymous: boolean;
  category: string;
  isPublic: boolean;
  isAnswered: boolean;
  helpfulCount: number;
  sortOrder: string;
}

const defaultEditState: EditState = {
  questionText: '',
  answerText: '',
  askedByUser: '',
  askedByDisplayName: '',
  askedByEmail: '',
  displayAsAnonymous: true,
  category: 'General',
  isPublic: false,
  isAnswered: false,
  helpfulCount: 0,
  sortOrder: '',
};

const ResourceComplianceQuestionsAdmin: React.FC = () => {
  const [records, setRecords] = useState<ResourceComplianceQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editRecord, setEditRecord] = useState<ResourceComplianceQuestion | null>(null);
  const [editState, setEditState] = useState<EditState>(defaultEditState);
  const [saving, setSaving] = useState(false);

  const [deleteRecord, setDeleteRecord] = useState<ResourceComplianceQuestion | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<ResourceComplianceQuestion[]>(
        '/api/resource-compliance/help/admin/questions'
      );
      setRecords(data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (unansweredOnly && r.isAnswered) return false;
      if (!search.trim()) return true;

      const q = search.toLowerCase();
      return (
        (r.questionText ?? '').toLowerCase().includes(q) ||
        (r.answerText ?? '').toLowerCase().includes(q) ||
        (r.category ?? '').toLowerCase().includes(q) ||
        (r.askedByDisplayName ?? '').toLowerCase().includes(q) ||
        (r.askedByEmail ?? '').toLowerCase().includes(q)
      );
    });
  }, [records, unansweredOnly, search]);

  const openEdit = (record: ResourceComplianceQuestion) => {
    setEditRecord(record);
    setEditState({
      questionText: record.questionText ?? '',
      answerText: record.answerText ?? '',
      askedByUser: record.askedByUser ?? '',
      askedByDisplayName: record.askedByDisplayName ?? '',
      askedByEmail: record.askedByEmail ?? '',
      displayAsAnonymous: record.displayAsAnonymous,
      category: record.category ?? 'General',
      isPublic: record.isPublic,
      isAnswered: record.isAnswered,
      helpfulCount: record.helpfulCount ?? 0,
      sortOrder: record.sortOrder === null || record.sortOrder === undefined ? '' : String(record.sortOrder),
    });
  };

  const handleSave = async () => {
    if (!editRecord) return;
    if (!editState.questionText.trim()) {
      setError('Question text is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/api/resource-compliance/help/admin/questions/${editRecord.questionId}`, {
        questionText: editState.questionText.trim(),
        answerText: editState.answerText.trim() || null,
        askedByUser: editState.askedByUser.trim() || null,
        askedByDisplayName: editState.askedByDisplayName.trim() || null,
        askedByEmail: editState.askedByEmail.trim() || null,
        displayAsAnonymous: editState.displayAsAnonymous,
        category: editState.category.trim() || null,
        isPublic: editState.isPublic,
        isAnswered: editState.isAnswered,
        helpfulCount: Number.isFinite(editState.helpfulCount) ? editState.helpfulCount : 0,
        sortOrder: editState.sortOrder.trim() === '' ? null : Number(editState.sortOrder),
      });

      setSuccess('Question updated successfully.');
      setEditRecord(null);
      await load();
      window.setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;

    setDeleting(true);
    setError(null);
    try {
      await apiClient.delete(`/api/resource-compliance/help/admin/questions/${deleteRecord.questionId}`);
      setSuccess('Question deleted successfully.');
      setDeleteRecord(null);
      await load();
      window.setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Failed to delete question');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (val?: string | null) => (val ? new Date(val).toLocaleString() : '—');

  return (
    <div className="container-fluid p-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Resource Health Questions</h4>
          <p className="text-muted mb-0">Manage submitted questions and published FAQ/community content.</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="unanswered-only-toggle"
            label={<><FaFilter className="me-1" />Unanswered only</>}
            checked={unansweredOnly}
            onChange={(e) => setUnansweredOnly(e.target.checked)}
          />
          <Button variant="outline-secondary" size="sm" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <InputGroup className="mb-3" style={{ maxWidth: 500 }}>
        <InputGroup.Text><FaSearch /></InputGroup.Text>
        <Form.Control
          placeholder="Search by question, answer, category, or asker..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>}
      </InputGroup>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <p className="text-muted small mb-2">Showing {filtered.length} of {records.length} questions</p>
          <div className="table-responsive">
            <Table hover size="sm">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Category</th>
                  <th>Asker</th>
                  <th>Status</th>
                  <th>Helpful</th>
                  <th>Sort</th>
                  <th>Created</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">No questions found</td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.questionId} className={!r.isAnswered ? 'table-warning' : undefined}>
                      <td className="text-truncate" style={{ maxWidth: 360 }} title={r.questionText}>
                        {r.questionText}
                      </td>
                      <td>{r.category || <span className="text-muted fst-italic">—</span>}</td>
                      <td className="text-truncate" style={{ maxWidth: 200 }} title={r.askedByDisplayName ?? r.askedByEmail ?? ''}>
                        {r.displayAsAnonymous
                          ? <Badge bg="secondary">Anonymous</Badge>
                          : (r.askedByDisplayName || r.askedByEmail || <span className="text-muted fst-italic">—</span>)}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <Badge bg={r.isAnswered ? 'success' : 'warning'} text={r.isAnswered ? undefined : 'dark'}>
                            {r.isAnswered ? 'Answered' : 'Unanswered'}
                          </Badge>
                          <Badge bg={r.isPublic ? 'primary' : 'secondary'}>
                            {r.isPublic ? 'Public' : 'Private'}
                          </Badge>
                        </div>
                      </td>
                      <td>{r.helpfulCount}</td>
                      <td>{r.sortOrder ?? <span className="text-muted fst-italic">—</span>}</td>
                      <td>{formatDate(r.createDt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button variant="outline-primary" size="sm" title="Edit question" onClick={() => openEdit(r)}>
                            <FaEdit />
                          </Button>
                          <Button variant="outline-danger" size="sm" title="Delete question" onClick={() => setDeleteRecord(r)}>
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={!!editRecord} onHide={() => setEditRecord(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Edit Question #{editRecord?.questionId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-grid gap-3">
            <Form.Group>
              <Form.Label>Question</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editState.questionText}
                onChange={(e) => setEditState((s) => ({ ...s, questionText: e.target.value }))}
                maxLength={2000}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Answer</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editState.answerText}
                onChange={(e) => setEditState((s) => ({ ...s, answerText: e.target.value }))}
                maxLength={4000}
              />
            </Form.Group>

            <div className="row g-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    type="text"
                    value={editState.category}
                    onChange={(e) => setEditState((s) => ({ ...s, category: e.target.value }))}
                    maxLength={128}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Helpful Count</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={editState.helpfulCount}
                    onChange={(e) => setEditState((s) => ({ ...s, helpfulCount: Number(e.target.value || 0) }))}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={editState.sortOrder}
                    onChange={(e) => setEditState((s) => ({ ...s, sortOrder: e.target.value }))}
                    placeholder="Blank for community"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Asked By User</Form.Label>
                  <Form.Control
                    type="text"
                    value={editState.askedByUser}
                    onChange={(e) => setEditState((s) => ({ ...s, askedByUser: e.target.value }))}
                    maxLength={256}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Asked By Display Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editState.askedByDisplayName}
                    onChange={(e) => setEditState((s) => ({ ...s, askedByDisplayName: e.target.value }))}
                    maxLength={256}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Asked By Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editState.askedByEmail}
                    onChange={(e) => setEditState((s) => ({ ...s, askedByEmail: e.target.value }))}
                    maxLength={512}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-4">
              <Form.Check
                type="switch"
                id="edit-question-anonymous"
                label="Display as anonymous"
                checked={editState.displayAsAnonymous}
                onChange={(e) => setEditState((s) => ({ ...s, displayAsAnonymous: e.target.checked }))}
              />
              <Form.Check
                type="switch"
                id="edit-question-public"
                label="Public"
                checked={editState.isPublic}
                onChange={(e) => setEditState((s) => ({ ...s, isPublic: e.target.checked }))}
              />
              <Form.Check
                type="switch"
                id="edit-question-answered"
                label="Answered"
                checked={editState.isAnswered}
                onChange={(e) => setEditState((s) => ({ ...s, isAnswered: e.target.checked }))}
              />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRecord(null)} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheck className="me-1" />}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteRecord} onHide={() => setDeleteRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this question?
          <p className="text-muted mt-2 mb-0"><small>{deleteRecord?.questionText}</small></p>
          <p className="text-danger mt-2 mb-0"><small>This removes the question from active Resource Health content. The record is retained for audit purposes and cannot currently be restored from this page.</small></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRecord(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={() => void handleDelete()} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : <FaTrash className="me-1" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResourceComplianceQuestionsAdmin;
